/**
 * Integrations Page
 * =================
 * Integration partners and connectivity options.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Plug, Shield, Zap } from 'lucide-react';
import { Button } from '../../components/shared/Button';
import { IntegrationCard } from '../../components/shared/Card';
import { SectionHeader } from '../../components/shared/SectionBadge';
import { FadeReveal, StaggerReveal } from '../../components/animations/FadeReveal';
import { INTEGRATION_PARTNERS } from '../../utils/constants';

/**
 * Integrations Page Component
 * Showcases integration capabilities.
 * 
 * @returns {JSX.Element} Integrations page
 */
function Integrations() {
  const categories = ['All', 'CRM', 'SIS', 'LMS', 'Analytics'];
  const [activeCategory, setActiveCategory] = React.useState('All');
  
  const filteredPartners = activeCategory === 'All' 
    ? INTEGRATION_PARTNERS 
    : INTEGRATION_PARTNERS.filter(p => p.category === activeCategory);
  
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-dme-blue via-dme-blue-600 to-dme-blue-800 py-24 lg:py-32">
        <div className="container-dme relative z-10">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
                <Plug className="w-4 h-4 text-dme-orange" />
                Seamless Connectivity
              </span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                Connect Your Entire Stack
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
                DME-CPH integrates with your existing tools and systems for a 
                unified enrollment marketing experience.
              </p>
            </div>
          </FadeReveal>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Integration Features"
              title="Built for Connectivity"
              subtitle="Our platform is designed to work seamlessly with your existing infrastructure."
              align="center"
              className="mb-16"
            />
          </FadeReveal>
          
          <StaggerReveal 
            selector=".integration-feature"
            stagger={0.1}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            <div className="integration-feature text-center">
              <div className="w-16 h-16 rounded-2xl bg-dme-blue-50 flex items-center justify-center mx-auto mb-4">
                <Plug className="w-8 h-8 text-dme-blue" />
              </div>
              <h3 className="font-heading font-bold text-xl text-dme-blue-800 mb-2">
                One-Click Setup
              </h3>
              <p className="text-dme-gray-600">
                Connect your systems in minutes with our guided setup process. 
                No coding required.
              </p>
            </div>
            
            <div className="integration-feature text-center">
              <div className="w-16 h-16 rounded-2xl bg-dme-orange-50 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-dme-orange" />
              </div>
              <h3 className="font-heading font-bold text-xl text-dme-blue-800 mb-2">
                Real-Time Sync
              </h3>
              <p className="text-dme-gray-600">
                Data flows instantly between systems, ensuring you always have 
                the most up-to-date information.
              </p>
            </div>
            
            <div className="integration-feature text-center">
              <div className="w-16 h-16 rounded-2xl bg-dme-green-50 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-dme-green" />
              </div>
              <h3 className="font-heading font-bold text-xl text-dme-blue-800 mb-2">
                Enterprise Security
              </h3>
              <p className="text-dme-gray-600">
                Bank-grade encryption and compliance with FERPA, GDPR, and 
                other regulations.
              </p>
            </div>
          </StaggerReveal>
        </div>
      </section>
      
      {/* Partners Grid */}
      <section className="section bg-dme-gray-50">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Integration Partners"
              title="Works With Your Favorite Tools"
              subtitle="Connect with the platforms you already use and love."
              align="center"
              className="mb-8"
            />
          </FadeReveal>
          
          {/* Category Filter */}
          <FadeReveal delay={0.1}>
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === category
                      ? 'bg-dme-blue text-white'
                      : 'bg-white text-dme-gray-600 hover:bg-dme-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </FadeReveal>
          
          {/* Partners Grid */}
          <StaggerReveal 
            selector=".partner-card"
            stagger={0.05}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {filteredPartners.map((partner, index) => (
              <div key={partner.name} className="partner-card">
                <IntegrationCard
                  name={partner.name}
                  category={partner.category}
                  status={partner.status}
                />
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>
      
      {/* API Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeReveal direction="left">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-dme-orange-50 rounded-full text-dme-orange text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  Developer Friendly
                </span>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl text-dme-blue-800 mb-4">
                  Powerful API for Custom Integrations
                </h2>
                <p className="text-dme-gray-600 mb-6 leading-relaxed">
                  Build custom integrations with our comprehensive REST API. 
                  Full documentation, SDKs, and developer support included.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {[
                    'RESTful API with JSON responses',
                    'Webhook support for real-time events',
                    'Comprehensive documentation',
                    'SDKs for popular languages',
                    'Sandbox environment for testing',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-dme-gray-600">
                      <Check className="w-5 h-5 text-dme-green flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <Link to="/contact">
                  <Button variant="primary" rightIcon={<ArrowRight className="w-5 h-5" />}>
                    Get API Access
                  </Button>
                </Link>
              </div>
            </FadeReveal>
            
            <FadeReveal direction="right">
              <div className="bg-dme-blue-800 rounded-2xl p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <pre className="text-sm text-green-400 overflow-x-auto">
                  <code>{`// Example API Request
const response = await fetch(
  'https://api.dmecph.com/v1/enrollments',
  {
    method: 'GET',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    }
  }
);

const data = await response.json();
console.log(data.enrollments);`}</code>
                </pre>
              </div>
            </FadeReveal>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Integrations;
